import { TAU, WHITE, circle, glow, polygon, rim } from '../paint';
import type { Ctx, Piece } from '../paint';
import { REFERENCE_RADIUS } from '../textures-looks';

export const UNIT = 2;

const ORANGE = '#ff9a2e';
const TUFT = '#ffb32e';
const CHICK_BODY = '#ffd84a';
const CHICK_HEAD = '#ffe066';
const EYE = '#1a1222';
const CHEEK = 'rgba(255,140,170,.75)';
const OUT = 'rgba(4,6,18,.9)';

const BODY = { along: -1, width: 10, height: 11.5, puffs: 14, puff: 3 } as const;
export const WINGS = { across: 10, rest: 0.35, beat: 0.3 } as const;
export const FEET = { along: 6, across: 6, stride: 5 } as const;
export const HOOD = { along: 3, radius: 7.4, tuft: -7.8 } as const;
export const CHICK_FEET = { along: -1, across: 2.4, stride: 1 } as const;

function units(draw: (ctx: Ctx) => void): (ctx: Ctx) => void {
  return (ctx) => {
    ctx.save();
    ctx.scale(UNIT, UNIT);
    draw(ctx);
    ctx.restore();
  };
}

function fillRim(ctx: Ctx, fill: string, width: number): void {
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.lineWidth = width;
  ctx.lineJoin = 'round';
  ctx.strokeStyle = OUT;
  ctx.stroke();
}

function puffs(ctx: Ctx, draw: () => void): void {
  for (let index = 0; index < BODY.puffs; index += 1) {
    const angle = (index * TAU) / BODY.puffs;
    circle(
      ctx,
      BODY.puff,
      BODY.along + Math.cos(angle) * BODY.width,
      Math.sin(angle) * BODY.height,
    );
    draw();
  }
}

function bodyPath(ctx: Ctx): void {
  ctx.beginPath();
  ctx.ellipse(BODY.along, 0, BODY.width, BODY.height, 0, 0, TAU);
}

function body(ctx: Ctx): void {
  puffs(ctx, () => {
    ctx.fillStyle = '#ececec';
    ctx.fill();
    rim(ctx, 1.2);
  });
  bodyPath(ctx);
  ctx.fillStyle = '#ececec';
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(1, 0, 5, 7, 0, 0, TAU);
  ctx.fillStyle = WHITE;
  ctx.fill();
}

function silhouette(ctx: Ctx): void {
  ctx.fillStyle = WHITE;
  puffs(ctx, () => {
    ctx.fill();
  });
  bodyPath(ctx);
  ctx.fill();
}

function wing(side: number): (ctx: Ctx) => void {
  return (ctx) => {
    polygon(ctx, [
      [3, 0],
      [-1, side * 8.5],
      [-4, side * 9.5],
      [-6, side * 7],
      [-5, side * 3],
    ]);
    ctx.fillStyle = '#ececec';
    ctx.fill();
    ctx.lineJoin = 'round';
    rim(ctx, 1.2);
  };
}

function foot(ctx: Ctx): void {
  ctx.beginPath();
  ctx.ellipse(0, 0, 3.8, 2.5, 0, 0, TAU);
  fillRim(ctx, ORANGE, 1);
}

function tuft(ctx: Ctx): void {
  for (let index = 0; index < 3; index += 1) {
    ctx.beginPath();
    ctx.ellipse(0, (index - 1) * 2, 2.6, 1, (index - 1) * 0.5, 0, TAU);
    fillRim(ctx, TUFT, 0.7);
  }
}

function hood(ctx: Ctx): void {
  circle(ctx, HOOD.radius);
  ctx.fillStyle = WHITE;
  ctx.fill();
  rim(ctx, 1.4);
}

function face(ctx: Ctx): void {
  polygon(ctx, [
    [5.8, -2.3],
    [10.6, 0],
    [5.8, 2.3],
  ]);
  fillRim(ctx, ORANGE, 1);
  for (const side of [-1, 1]) {
    circle(ctx, 1.15, 4.2, side * 3.5);
    ctx.fillStyle = EYE;
    ctx.fill();
    circle(ctx, 0.4, 4.6, side * 3.5 - 0.3);
    ctx.fillStyle = WHITE;
    ctx.fill();
    circle(ctx, 1.2, 2.4, side * 5.2);
    ctx.fillStyle = CHEEK;
    ctx.fill();
  }
}

function chick(ctx: Ctx): void {
  circle(ctx, 4.2, -0.8, 0);
  fillRim(ctx, CHICK_BODY, 1);
  circle(ctx, 2.8, 2.6, 0);
  fillRim(ctx, CHICK_HEAD, 0.9);
  polygon(ctx, [
    [4.9, -1.1],
    [6.9, 0],
    [4.9, 1.1],
  ]);
  fillRim(ctx, ORANGE, 0.5);
  for (const side of [-1, 1]) {
    circle(ctx, 0.6, 3.4, side * 1.3);
    ctx.fillStyle = EYE;
    ctx.fill();
  }
}

function chickFoot(ctx: Ctx): void {
  ctx.beginPath();
  ctx.ellipse(0, 0, 1.5, 0.9, 0, 0, TAU);
  ctx.fillStyle = ORANGE;
  ctx.fill();
}

function dust(ctx: Ctx): void {
  circle(ctx, 2.4);
  ctx.fillStyle = WHITE;
  ctx.fill();
}

function soft(width: number, height: number): Piece {
  return [
    96,
    96,
    REFERENCE_RADIUS,
    (ctx) => {
      glow(ctx, WHITE, 8);
      ctx.fillStyle = WHITE;
      ctx.beginPath();
      ctx.ellipse(0, 0, width, height, 0, 0, TAU);
      ctx.fill();
    },
  ];
}

export const CHICK_PIECES = {
  chick: [32, 24, REFERENCE_RADIUS, units(chick)],
  chickFoot: [10, 8, REFERENCE_RADIUS, units(chickFoot)],
  dust: [14, 14, REFERENCE_RADIUS, units(dust)],
} satisfies Record<string, Piece>;

export const POUSSIN_PIECES = {
  ...CHICK_PIECES,
  body: [64, 64, REFERENCE_RADIUS, units(body)],
  silhouette: [64, 64, REFERENCE_RADIUS, units(silhouette)],
  wingLeft: [30, 44, REFERENCE_RADIUS, units(wing(-1))],
  wingRight: [30, 44, REFERENCE_RADIUS, units(wing(1))],
  foot: [20, 16, REFERENCE_RADIUS, units(foot)],
  tuft: [16, 18, REFERENCE_RADIUS, units(tuft)],
  hood: [36, 36, REFERENCE_RADIUS, units(hood)],
  face: [48, 32, REFERENCE_RADIUS, units(face)],
  shadow: soft(22, 26),
} satisfies Record<string, Piece>;
